import { useState, useEffect, useCallback } from 'react';
import {
  getNudgePreferences,
  updateNudgePreferences,
  snoozeNudges,
} from '../services/nudge.service';

/**
 * Hook: load and mutate a member's nudge preferences.
 *
 * Returns:
 *   prefs      — nudge preferences object (or null while loading)
 *   isLoading
 *   error
 *   save       — update one or more preference fields
 *   snooze     — snooze until ISO timestamp (or null to clear)
 */
export function useNudgePrefs() {
  const [prefs, setPrefs]     = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]     = useState(null);

  const fetch = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getNudgePreferences();
      setPrefs(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load nudge preferences.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const save = useCallback(async (fields) => {
    const updated = await updateNudgePreferences(fields);
    setPrefs(updated);
    return updated;
  }, []);

  const snooze = useCallback(async (until) => {
    const result = await snoozeNudges(until);
    setPrefs(prev => prev ? { ...prev, snooze_until: result.snooze_until } : prev);
    return result;
  }, []);

  return { prefs, isLoading, error, save, snooze, refresh: fetch };
}
